import {useStore} from "vue2-helpers/vuex";

export const formatNumber = (string) => string.toLocaleString()

const formatNumberShort = (number) => {
    return Intl.NumberFormat("en", {notation: "compact", maximumSignificantDigits: 3}).format(number)
}

// Smart toFixed: keep specified decimals but strip trailing zeros and the dot if unnecessary
export const toFixedSmart = (num, digits = 1) => {
    const fixed = Number(num).toFixed(digits);
    // remove trailing zeros and optional trailing dot
    return fixed.replace(/(\.\d*?)0+$/,'$1').replace(/\.$/, '');
}

export const formatThousands = (num) => {
    if (num >= 1e9) {
        return toFixedSmart(num / 1e9, 1) + 'B';
    } else if (num >= 1e6) {
        return toFixedSmart(num / 1e6, 1) + 'M';
    } else if (num >= 1e3) {
        return toFixedSmart(num / 1e3, 1) + 'K';
    } else {
        return num.toString();
    }
}

export function sanitizeUrl(keyWord) {
    try {
        keyWord = (new URL(keyWord)).hostname;
    } catch(e) {
        return keyWord;
    }

    keyWord = keyWord.replace(/^(www\.)/,"");
    return keyWord;
}

export const getPermissionNamesForDisplay = (permission, maxCount = 2) => {
    let array = []
    let num = 0

    permission.forEach(item => {
        if (num < maxCount) {
            array.push(item.name)
            ++num
        }
    })

    if (num < permission.length) {
        array.push('+ ' + (permission.length - num) + ' more')
    }

    return array
}

export const getAvatarsForDisplay = (permissions, maxCount = 4) => {
    let array = [];

    // First, add users with avatars
    permissions.forEach(item => {
        if (array.length === maxCount) {
            return
        }

        if (item.type === "user" && item.avatar?.length) {
            array.push({'name': item.name, 'avatar': item.avatar, 'type': item.type});
        }
    })

    permissions = permissions.filter(item => !array.some(i => i.name === item.name && i.type === item.type));

    // Then, add users without avatars and groups
    permissions.forEach(item => {
        if (array.length === maxCount) {
            return;
        }

        if (item.type === "group") {
            if (item.id === -1) {
                item.avatar = require('../images/icon-enterprise-blue-small.svg');
            } else {
                item.avatar = require('../images/icon-team-light-blue.svg');
            }
        } else if (!item.avatar?.length) {
            // item.avatar = require('../images/icon-user-blue.svg');
        }

        array.push({'name': item.name, 'avatar': item.avatar, 'type': item.type});
    })

    return array
}

export const getShortFullName = (fullName) => {
    let names = fullName.split(/\s+/);
    if (names.length > 1) {
        names[names.length -1] = names[names.length -1 ].substr(0, 1) + ".";
        return names.join(' ');
    } else {
        return fullName
    }
}

export const validEmail = (email) => {
    const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
}

export const validUrl = (url) => {
    if (!url || typeof url !== 'string') return false;

    const normalized = /^https?:\/\//i.test(url) ? url : 'https://' + url;

    try {
        const { hostname } = new URL(normalized);
        return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(hostname);
    } catch {
        return false;
    }
}

const ucWords = (str) => {
    return str.replace(/^(.)|\s(.)/g, ( $1 ) => { return $1.toUpperCase ( ); } );
}

export const ucFirst = (str) => {
    if (str) {
        return str[0].toUpperCase() + str.substring(1)
    }
}

export const formattedFundingSeries = (fundingType, series) => {
    let val = ''

    if (['angel','convertible_note','pre_seed'].indexOf(fundingType) >= 0) {
        val = "Angel"
    }
    else if (fundingType === 'equity_crowdfunding') {
        val = "Crowdfunding"
    }
    else if (fundingType === "seed") {
        val = "Seed"
    }

    else if (fundingType === "venture") {

        if (!series || !series.length) {
            val = "Venture - Unspecified";
        }
        else if (['A','B','C', 'D','E','F','G', 'H','I','J'].indexOf(series) >= 0) {
            val = "Series " + series;
        }
    }
    else if (fundingType) {
        val = ucWords(fundingType.replace(/_/g, ' '))
    }
    else {
        val = fundingType
    }

    return val
}

const formattedFundingSeriesArray = (fundingTypes) => {
    let array = []
    fundingTypes.forEach(item => {
        array.push(formattedFundingSeries(item.funding_type, item.series))
    })

    return array
}

export const cutString = (str, maxLen, ellipsis = '') => {
    if (str.length <= maxLen) {
        return str;
    }
    else {
        return str.substr(0, str.lastIndexOf(' ', maxLen)) + ellipsis;
    }
}

export const completeLink = (str) => {
    if (str.substr(0, 7) != 'http://' && str.substr(0, 8) != 'https://') {
        str = 'http://' + str
    }

    return str
}

export const fieldFilled = (field) => {
    return (!!field?.text?.length || !!field?.options?.length || !!field?.links?.length || !!field?.files?.length)
}

const trimSymbol = (str, symbol) => {
    if (str.substr(0, 1) == symbol) {
        str = str.substr(1, str.length)
    }

    if (str[str.length - 1] == symbol) {
        str = str.substr(0, str.length - 1)
    }

    return str
}

export const trackerAccess = (tracker) => {
    let parts = []
    let accessType = tracker.accessType || tracker.access_type;

    if (accessType === 'private') {
        parts.push('None')
    }
    else if (accessType === 'all') {
        parts.push('Shared with All Ecosystems')
    }
    else {
        if (accessType === 'categories') {
            parts.push('Shared with Ecosystems by Category')
        }
        else if (accessType === 'connections') {
            parts.push('Shared with Connected ecosystems')
        }

        let ecosystemsCount = 0

        if (typeof tracker.ecosystems_count !== 'undefined') {
            ecosystemsCount = tracker.ecosystems_count
        }
        else if (typeof tracker.ecosystems !== 'undefined' && tracker.ecosystems.length) {
            ecosystemsCount = tracker.ecosystems.length
        }

        if (ecosystemsCount > 0) {
            if (parts.length) {
                parts.push(ecosystemsCount + ' ' + getNoun('Other', ecosystemsCount))
            }
            else {
                parts.push('Shared with ' + ecosystemsCount + ' ' + getNoun('ecosystem', ecosystemsCount) )
            }
        }
    }

    return parts.join(' + ')
}

const sortByLastName = (list) => {
    list.sort((a, b) => {
        let words1 = a.name.split(" ", 2)
        let words2 = b.name.split(" ", 2)
        return (words1.length == 2 ? words1[1] : words1[0]).localeCompare(words2.length == 2 ? words2[1] : words2[0])
    })
}

export const getNoun = (noun, number, plural = null) => {
    if (parseInt(number) === 1) return noun;
    if (plural) return plural;
    return noun + "s";
}

export const getNumberWithNoun = (noun, number, plural = null) => {
    let str = number + " ";
    if (parseInt(number) === 1) {
        str += noun;
    } else {
        if (plural) {
            str += plural;
        } else {
            str += noun + "s";
        }
    }

    return str;
}

export const toLocale = (value) => value.toLocaleString()

export const isYoutubeLink = (href) => {
    return !!href.includes('youtube.com/watch?v=');
}

export const getYoutubeEmbeddedUrl = (href) => {
    let hash = href.substring(href.indexOf('=') + 1, href.indexOf('&') >= 0 ? href.indexOf('&') : href.length);

    if (hash) {
        return 'https://www.youtube.com/embed/' + hash;
    }
}

const hasMultipleWords = (phrase) => phrase.trim().split(/\s+/).length > 1

export const titleCase = (string) => {
    return string.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}

export const snakeToCamelCase = (string) => {
    return string.toLowerCase().replace(/(_\w)/g, match => match[1].toUpperCase());
}

export const stripTags = (string) => string.replace(/<\/?[^>]+(>|$)/g, "");

export const stripPaste = (e) => {
    const text = (e.originalEvent || e).clipboardData.getData('text/plain');

    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    selection.deleteFromDocument();

    const textNode = document.createTextNode(text);
    selection.getRangeAt(0).insertNode(textNode);

    selection.collapseToEnd();

    try {
        let node = selection.anchorNode;
        // if anchorNode is a text node, use its parentElement
        let el = node && node.nodeType === Node.TEXT_NODE ? node.parentElement : node;

        // climb up until we find a contenteditable element (or null)
        while (el && el.nodeType === 1 && el.getAttribute && el.getAttribute('contenteditable') !== 'true') {
            el = el.parentElement;
        }

        if (el) {
            const evt = new Event('input', { bubbles: true });
            el.dispatchEvent(evt);
        }
    } catch (err) {
        // ignore failures silently
    }
}

export const createStartupLabel = (text, store = null) => {
    if (!store) {
        store = useStore();

        if (!store.getters.isDaikinEnterprise) {
            return text;
        }
    }

    return text.replace(/startup(s)?/gi, (match, plural) => {
        const isCapitalized = match[0] === match[0].toUpperCase();
        let replacement = plural ? 'companies' : 'company';

        return isCapitalized
            ? replacement.charAt(0).toUpperCase() + replacement.slice(1)
            : replacement;
    });
}

// Join an array of strings into a human-friendly list using "and".
// Examples:
// [] -> ''
// ['A'] -> 'A'
// ['A','B'] -> 'A and B'
// ['A','B','C'] -> 'A, B, and C'
export const joinWithAnd = (items = []) => {
    if (!Array.isArray(items)) return '';
    const len = items.length;
    if (len === 0) return '';
    if (len === 1) return String(items[0]);
    if (len === 2) return `${items[0]} and ${items[1]}`;
    const allButLast = items.slice(0, -1).join(', ');
    return `${allButLast}, and ${items[len - 1]}`;
}

export default {
    formatNumber,
    formatNumberShort,
    formatThousands,
    getPermissionNamesForDisplay,
    getAvatarsForDisplay,
    getShortFullName,
    validEmail,
    ucWords,
    formattedFundingSeries,
    formattedFundingSeriesArray,
    cutString,
    completeLink,
    fieldFilled,
    trimSymbol,
    ucFirst,
    trackerAccess,
    sortByLastName,
    getNoun,
    toLocale,
    isYoutubeLink,
    getYoutubeEmbeddedUrl,
    hasMultipleWords,
    stripTags,
    toFixedSmart
}
